(() => {
  const quickViewRequests = new Map();
  let activeQuickViewRequest;
  const cancelIfLeaving = event => {
    const request = activeQuickViewRequest;
    if (request && !request.trigger.contains(event.target)) request.cancel();
  };
  document.addEventListener('pointerdown', cancelIfLeaving, {passive: true});
  document.addEventListener('focusin', cancelIfLeaving);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') activeQuickViewRequest?.cancel();
  });
  const loadQuickView = card => {
    const url = new URL(card.dataset.pawQuickUrl, window.location.origin);
    if (url.origin !== window.location.origin) return Promise.reject(new Error('Invalid product URL'));
    url.searchParams.set('section_id', 'paw-quick-view');
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const key = url.href;
    if (quickViewRequests.has(key)) {
      const request = quickViewRequests.get(key);
      quickViewRequests.delete(key);
      quickViewRequests.set(key, request);
      return request;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const request = fetch(url.href, {credentials: 'same-origin', signal: controller.signal})
      .then(response => {
        if (!response.ok) throw new Error('Quick view request failed');
        return response.text();
      })
      .then(html => {
        const page = new DOMParser().parseFromString(html, 'text/html');
        const template = page.querySelector('template[data-paw-quick-view-fragment]');
        if (!template?.content.querySelector('[data-paw-view-id]')) throw new Error('Quick view content missing');
        return template;
      })
      .catch(error => {
        if (quickViewRequests.get(key) === request) quickViewRequests.delete(key);
        throw error;
      })
      .finally(() => window.clearTimeout(timeout));
    quickViewRequests.set(key, request);
    if (quickViewRequests.size > 32) quickViewRequests.delete(quickViewRequests.keys().next().value);
    return request;
  };
  const init = section => {
    if (section.dataset.pawBestReady) return;
    section.dataset.pawBestReady = 'true';
    const rail = section.querySelector('[data-paw-best-rail]');
    const prev = section.querySelector('[data-paw-best-prev]');
    const next = section.querySelector('[data-paw-best-next]');
    const dialog = section.querySelector('[data-paw-quick-dialog]');
    if (!rail || !dialog) return;

    const prefetch = event => {
      const trigger = event.target.closest('[data-paw-quick-view]');
      const card = trigger?.closest('[data-paw-best-card][data-paw-quick-url]');
      if (card && rail.contains(card)) loadQuickView(card).catch(() => {});
    };
    rail.addEventListener('pointerover', prefetch, {passive: true});
    rail.addEventListener('pointerdown', prefetch, {passive: true});
    rail.addEventListener('focusin', prefetch);
    const cancelPending = () => {
      dialog._pawQuickPending?.removeAttribute('aria-busy');
      dialog._pawQuickPending = null;
      dialog._pawQuickRequest = null;
      if (activeQuickViewRequest?.dialog === dialog) activeQuickViewRequest = null;
    };

    const updateArrows = () => {
      const remaining = rail.scrollWidth - rail.clientWidth;
      prev.hidden = remaining < 2;
      next.hidden = remaining < 2;
      prev.disabled = false;
      next.disabled = false;
    };
    const move = direction => {
      const remaining = rail.scrollWidth - rail.clientWidth;
      if (remaining < 2) return;
      if (direction < 0 && rail.scrollLeft <= 2) {
        rail.scrollTo({left: remaining, behavior: 'smooth'});
        return;
      }
      if (direction > 0 && rail.scrollLeft >= remaining - 2) {
        rail.scrollTo({left: 0, behavior: 'smooth'});
        return;
      }
      const card = rail.querySelector('.paw-best-card');
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 18;
      const distance = card ? card.getBoundingClientRect().width + gap : 300;
      rail.scrollBy({left: direction * distance * (innerWidth < 750 ? 1 : 2), behavior: 'smooth'});
    };
    if (prev && next) {
      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', updateArrows, {passive: true});
      window.addEventListener('resize', updateArrows);
      requestAnimationFrame(updateArrows);
    }

    const updateVariant = selected => {
      dialog.querySelector('[data-paw-view-id]').value = selected.value;
      dialog.querySelector('[data-product-form]').dataset.variantAvailable = String(!selected.disabled);
      dialog.querySelector('[data-paw-view-price]').hidden = false;
      const productLink = dialog.querySelector('[data-paw-view-product-link]');
      if (productLink) {
        const productUrl = new URL(productLink.href, window.location.origin);
        productUrl.searchParams.set('variant', selected.value);
        productLink.href = productUrl.pathname + productUrl.search + productUrl.hash;
      }
      dialog.querySelector('[data-paw-view-price]').textContent = selected.dataset.price;
      const compare = dialog.querySelector('[data-paw-view-compare]');
      const discount = dialog.querySelector('[data-paw-view-discount]');
      compare.hidden = !selected.dataset.compare;
      compare.textContent = selected.dataset.compare || '';
      discount.hidden = !selected.dataset.discount;
      discount.textContent = selected.dataset.discount || '';
      dialog.querySelectorAll('[data-paw-view-choice]').forEach(button => {
        const active = button === selected;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const select = dialog.querySelector('[data-paw-view-variant]');
      if (select && select.value !== selected.value) select.value = selected.value;
      const optionValues = selected.dataset.options ? JSON.parse(selected.dataset.options) : [];
      dialog.querySelectorAll('[data-paw-option-group]').forEach(group => {
        const current = optionValues[Number(group.dataset.index)];
        const label = group.querySelector('[data-paw-option-current]');
        if (label) label.textContent = current || '';
        group.querySelectorAll('[data-paw-option]').forEach(button => {
          const active = button.dataset.value === current;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
      const add = dialog.querySelector('.paw-quick-view__purchase [type="submit"]');
      add.disabled = selected.disabled;
      dialog.querySelector('[data-paw-add-label]').textContent = selected.disabled ? add.dataset.soldOutLabel : add.dataset.addLabel;
      const save = dialog.querySelector('.paw-quick-view__save');
      if (save) save.dataset.wishlistPrice = selected.dataset.price;
      const image = dialog.querySelector('.paw-quick-view__image');
      if (image && selected.dataset.image) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = selected.dataset.image;
        dialog.querySelectorAll('[data-paw-view-thumb]').forEach(button => {
          const active = button.dataset.image === selected.dataset.image;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }
    };
    rail.addEventListener('click', async event => {
      const thumb = event.target.closest('[data-paw-thumb]');
      if (thumb) {
        const card = thumb.closest('[data-paw-best-card]');
        if (thumb.dataset.variantId) {
          card.querySelectorAll('.paw-best-card__image-link, .paw-best-card__info h3 a').forEach(link => {
            const productUrl = new URL(link.href, window.location.origin);
            productUrl.searchParams.set('variant', thumb.dataset.variantId);
            link.href = productUrl.pathname + productUrl.search + productUrl.hash;
          });
        }
        const image = card.querySelector('.paw-best-card__image');
        if (image && thumb.dataset.image) {
          const preview = thumb.querySelector('img');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = preview?.currentSrc || preview?.src || thumb.dataset.image;
          image.alt = thumb.dataset.alt || '';
          const full = new Image();
          full.onload = () => {
            if (thumb.classList.contains('is-active')) image.src = full.src;
          };
          full.src = thumb.dataset.image;
        }
        card.querySelectorAll('[data-paw-thumb]').forEach(button => {
          const active = button === thumb;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        return;
      }
      const trigger = event.target.closest('[data-paw-quick-view]');
      if (!trigger) return;
      const card = trigger.closest('[data-paw-best-card]');
      if (!card?.dataset.pawQuickUrl) return;
      activeQuickViewRequest?.cancel();
      cancelPending();
      const request = {};
      dialog._pawQuickRequest = request;
      dialog._pawQuickPending = trigger;
      activeQuickViewRequest = {dialog, trigger, cancel: cancelPending};
      trigger.setAttribute('aria-busy', 'true');
      let template;
      try {
        template = await loadQuickView(card);
      } catch (_) {
        if (dialog._pawQuickRequest === request) {
          const toast = document.getElementById('ThemeToast');
          if (toast) {
            toast.textContent = card.dataset.pawQuickError;
            toast.classList.add('is-visible');
            window.clearTimeout(toast._timer);
            toast._timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
          }
          cancelPending();
        }
        return;
      }
      if (dialog._pawQuickRequest !== request || !trigger.isConnected) return;
      cancelPending();
      const content = dialog.querySelector('[data-paw-quick-content]');
      content.replaceChildren(template.content.cloneNode(true));
      const badge = content.querySelector('.paw-quick-view__photo-badge');
      if (badge) badge.textContent = card.dataset.pawQuickBadge;
      content.scrollTop = 0;
      const selectedThumb = card.querySelector('[data-paw-thumb].is-active[data-variant-id]');
      if (selectedThumb) {
        const choice = [...content.querySelectorAll('[data-paw-view-choice]')].find(button => button.value === selectedThumb.dataset.variantId && !button.disabled);
        const select = content.querySelector('[data-paw-view-variant]');
        if (choice) {
          updateVariant(choice);
        } else if (select && [...select.options].some(option => option.value === selectedThumb.dataset.variantId && !option.disabled)) {
          select.value = selectedThumb.dataset.variantId;
          updateVariant(select.selectedOptions[0]);
        }
      }
      dialog._trigger = trigger;
      dialog.showModal();
      dialog.querySelector('[data-paw-quick-close]').focus();
    });
    dialog.addEventListener('click', event => {
      const quantityButton = event.target.closest('[data-paw-qty-minus], [data-paw-qty-plus]');
      if (quantityButton) {
        const input = dialog.querySelector('[data-paw-quantity]');
        const step = quantityButton.hasAttribute('data-paw-qty-plus') ? 1 : -1;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
        return;
      }
      const optionButton = event.target.closest('[data-paw-option]');
      if (optionButton) {
        const groups = [...dialog.querySelectorAll('[data-paw-option-group]')];
        const values = groups.map(group => group.querySelector('[data-paw-option][aria-pressed="true"]')?.dataset.value);
        const index = Number(optionButton.dataset.index);
        values[index] = optionButton.dataset.value;
        const entries = [...dialog.querySelectorAll('[data-paw-view-choice]')];
        const select = dialog.querySelector('[data-paw-view-variant]');
        if (select) entries.push(...select.options);
        const match = entry => {
          const options = JSON.parse(entry.dataset.options || '[]');
          return options.every((value, i) => value === values[i]);
        };
        const variant = entries.find(match);
        if (variant) updateVariant(variant);
        else {
          groups.forEach((group, i) => {
            group.querySelector('[data-paw-option-current]').textContent = values[i] || '';
            group.querySelectorAll('[data-paw-option]').forEach(button => {
              const active = button.dataset.value === values[i];
              button.classList.toggle('is-active', active);
              button.setAttribute('aria-pressed', String(active));
            });
          });
          dialog.querySelector('[data-paw-view-id]').value = '';
          const form = dialog.querySelector('[data-product-form]');
          form.dataset.variantAvailable = 'false';
          form.querySelector('[type="submit"]').disabled = true;
          dialog.querySelector('[data-paw-add-label]').textContent = form.dataset.unavailableLabel;
          ['[data-paw-view-price]', '[data-paw-view-compare]', '[data-paw-view-discount]'].forEach(selector => { dialog.querySelector(selector).hidden = true; });
        }
        return;
      }
      const choice = event.target.closest('[data-paw-view-choice]');
      if (choice) {
        updateVariant(choice);
        return;
      }
      const thumb = event.target.closest('[data-paw-view-thumb]');
      if (!thumb) return;
      const image = dialog.querySelector('.paw-quick-view__image');
      if (image && thumb.dataset.image) {
        const preview = thumb.querySelector('img');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = preview?.currentSrc || preview?.src || thumb.dataset.image;
        image.alt = thumb.dataset.alt || '';
        const full = new Image();
        full.onload = () => {
          if (thumb.classList.contains('is-active')) image.src = full.src;
        };
        full.src = thumb.dataset.image;
      }
      dialog.querySelectorAll('[data-paw-view-thumb]').forEach(button => {
        const active = button === thumb;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    });
    dialog.addEventListener('change', event => {
      const select = event.target.closest('[data-paw-view-variant]');
      if (select) updateVariant(select.selectedOptions[0]);
      const quantity = event.target.closest('[data-paw-quantity]');
      if (quantity) quantity.value = Math.max(1, parseInt(quantity.value, 10) || 1);
    });
    dialog.querySelector('[data-paw-quick-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('submit', event => { if (event.target.matches('[data-product-form]')) dialog.close(); });
    dialog.addEventListener('close', () => { cancelPending(); dialog._trigger?.focus(); });
  };
  document.querySelectorAll('.paw-bestsellers[data-paw-shelf], .collection-main[data-paw-shelf]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches('.paw-bestsellers[data-paw-shelf], .collection-main[data-paw-shelf]')) init(event.target);
    event.target.querySelectorAll('.paw-bestsellers[data-paw-shelf], .collection-main[data-paw-shelf]').forEach(init);
  });
})();

